import { useCallback } from 'react';
import type { Lang } from '@/shared/domain/types';
import { useAppSelector } from '@/shared/store/hooks';
import { commonMsgs } from './common';
import {
  translate,
  type MessageDef,
  type MessageKey,
  type MessageTree,
  type TParams,
} from './define';

export interface TFunction<E extends MessageTree> {
  (key: MessageKey<E>, params?: TParams): string;
}

export interface UseT<E extends MessageTree> {
  t: TFunction<E>;
  lang: Lang;
  /** Traduce un texto de error de la API: si es una clave `errors.*` la traduce; si no, lo devuelve tal cual. */
  te: (textOrKey: string) => string;
}

export function useT(): UseT<(typeof commonMsgs)['es']>;
export function useT<E extends MessageTree>(def: MessageDef<E>): UseT<E>;
export function useT(def: MessageDef = commonMsgs as unknown as MessageDef): UseT<MessageTree> {
  const lang = useAppSelector((s) => s.lang.current);
  const t = useCallback(
    (key: string, params?: TParams) => translate(def, lang, key, params),
    [def, lang],
  );
  const te = useCallback(
    (textOrKey: string) =>
      textOrKey.startsWith('errors.')
        ? translate(commonMsgs as unknown as MessageDef, lang, textOrKey)
        : textOrKey,
    [lang],
  );
  return { t: t as TFunction<MessageTree>, lang, te };
}
